import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Button, 
  TextField, 
  FormControl, 
  FormLabel, 
  RadioGroup, 
  Radio, 
  FormControlLabel,
  Slider, 
  Divider,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AdAnalytics from './AdAnalytics';
import { PromptSelector } from './PromptSelector';
import { PlacementSelector } from './PlacementSelector';
import { LLMResponseRenderer } from './LLMResponseRenderer';
import { playgroundApi, AdPreview } from '../../services/playgroundApi';
import { generateAdVariants, AdVariant } from '../../services/adService';
import TabPanel from '../common/TabPanel';
import AgentTaskSelector from './AgentTaskSelector';
import AgentOutputRenderer from './AgentOutputRenderer';
import { PromptOption, PlacementType } from '../../types/playground';

// Mock data for personas
const PERSONAS = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'enthusiastic', label: 'Enthusiastic' },
  { value: 'authoritative', label: 'Authoritative' },
  { value: 'creative', label: 'Creative' }
];

interface AdvertiserPlaygroundProps {
  onRoleSwitch: () => void;
  onReset: () => void;
}

const AdvertiserPlayground: React.FC<AdvertiserPlaygroundProps> = ({ onRoleSwitch, onReset }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [input, setInput] = useState('');
  const [persona, setPersona] = useState('professional');
  const [tone, setTone] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adVariants, setAdVariants] = useState<AdVariant[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedPrompt, setSelectedPrompt] = useState<PromptOption | null>(null);
  const [selectedPlacement, setSelectedPlacement] = useState<PlacementType | null>(null);
  const [previewData, setPreviewData] = useState<AdPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<string>('');
  const [agentOutput, setAgentOutput] = useState<any>(null);
  const [isAgentLoading, setIsAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const handlePersonaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPersona(e.target.value);
  };

  const handleToneChange = (_: Event, newValue: number | number[]) => {
    setTone(Array.isArray(newValue) ? newValue[0] : newValue);
  };

  const handleGenerateAds = async () => {
    setIsLoading(true);
    setError(null);
    setAdVariants([]);
    try {
      const variants = await generateAdVariants({
        product_description: input,
        persona,
        tone: marks.find(m => m.value === tone)?.label || 'Neutral',
        num_variants: 3
      });
      setAdVariants(variants);
    } catch (err) {
      setError('Failed to generate ads. Please try again.');
      console.error('Error generating ads:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyAd = (title: string) => {
    const variant = adVariants.find(v => v.title === title);
    if (variant) {
      const adText = `Title: ${variant.title}\nDescription: ${variant.description}\nCTA: ${variant.cta}`;
      navigator.clipboard.writeText(adText);
      setCopiedId(title);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const marks = [
    { value: 0, label: 'Professional' },
    { value: 50, label: 'Neutral' },
    { value: 100, label: 'Casual' },
  ];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handlePreview = async () => {
    if (!selectedPrompt || !selectedPlacement) {
      setPreviewError('Please select both a prompt and a placement');
      return;
    }

    setIsLoadingPreview(true);
    setPreviewError(null);

    try {
      const preview = await playgroundApi.getPreview(selectedPrompt.text, selectedPlacement);
      setPreviewData(preview);
    } catch (err) {
      setPreviewError('Failed to generate preview. Please try again.');
      console.error('Error generating preview:', err);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleTaskChange = (task: string) => {
    setSelectedTask(task);
    setAgentOutput(null);
    setAgentError(null);
  };

  const handleGenerateAgentOutput = async () => {
    setIsAgentLoading(true);
    setAgentError(null);

    try {
      const output = await playgroundApi.getAgentOutput(selectedTask);
      setAgentOutput(output);
    } catch (err) {
      setAgentError('Failed to generate agent output. Please try again.');
      console.error('Error generating agent output:', err);
    } finally {
      setIsAgentLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ color: 'white', fontWeight: 'bold' }}>
          Advertiser Playground
        </Typography>
        <Box>
          <Tooltip title="Switch to Publisher view">
            <Button 
              onClick={onRoleSwitch}
              startIcon={<SwapHorizIcon />}
              sx={{ mr: 2, color: '#10B981' }}
            >
              Switch to Publisher
            </Button>
          </Tooltip>
          <Tooltip title="Reset playground">
            <Button 
              onClick={onReset}
              startIcon={<RefreshIcon />}
              sx={{ color: '#94A3B8' }}
            >
              Reset
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ mt: 4, mb: 4 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="advertiser playground tabs"
          centered
        >
          <Tab label="Ad Campaign" />
          <Tab label="Analytics" />
          <Tab label="Contextual Ads" />
          <Tab label="AI Agent" />
        </Tabs>

        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 3, 
                  backgroundColor: '#1E293B',
                  borderRadius: 2,
                  height: '100%',
                  maxWidth: 420,
                  width: '100%',
                  boxSizing: 'border-box',
                  minWidth: 0,
                  overflow: 'auto',
                  margin: '0 auto'
                }}
              >
                <Typography variant="h6" sx={{ color: 'white', mb: 3 }}>Ad Generator</Typography>
                
                {error && (
                  <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}>
                    {error}
                  </Alert>
                )}
                
                <TextField
                  fullWidth
                  label="Product or Service Description"
                  placeholder="Describe your product or service..."
                  multiline
                  rows={4}
                  value={input}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  sx={{ 
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'rgba(148, 163, 184, 0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(148, 163, 184, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#3B82F6' }
                    },
                    '& .MuiInputBase-input': { color: 'white' },
                    '& .MuiInputLabel-root': { color: '#94A3B8' }
                  }}
                />
                
                <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }} disabled={isLoading}>
                  <FormLabel component="legend" sx={{ color: '#94A3B8', mb: 1 }}>Select Persona</FormLabel>
                  <RadioGroup 
                    row 
                    value={persona} 
                    onChange={handlePersonaChange}
                    sx={{ justifyContent: 'space-between' }}
                  >
                    {PERSONAS.map((persona) => (
                      <FormControlLabel 
                        key={persona.value} 
                        value={persona.value} 
                        control={<Radio sx={{ color: '#94A3B8', '&.Mui-checked': { color: '#3B82F6' } }} />} 
                        label={persona.label}
                        sx={{ color: '#CBD5E1' }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                
                <Box sx={{ mb: 5 }}>
                  <Typography sx={{ color: '#94A3B8', mb: 1 }}>Tone Adjustment</Typography>
                  <Slider
                    value={tone}
                    onChange={handleToneChange}
                    aria-labelledby="tone-slider"
                    marks={marks}
                    disabled={isLoading}
                    sx={{ 
                      color: '#3B82F6',
                      '& .MuiSlider-mark': { backgroundColor: '#64748B' },
                      '& .MuiSlider-markLabel': { color: '#94A3B8' }
                    }}
                  />
                </Box>
                
                <Button 
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleGenerateAds}
                  disabled={!input || isLoading}
                  sx={{ 
                    backgroundColor: '#3B82F6',
                    '&:hover': { backgroundColor: '#2563EB' },
                    '&.Mui-disabled': { backgroundColor: 'rgba(59, 130, 246, 0.3)' }
                  }}
                >
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Generate Ad Variants'}
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper 
                elevation={3} 
                sx={{ 
                  backgroundColor: '#1E293B',
                  borderRadius: 2,
                  pb: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
                  {adVariants.length === 0 ? (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '100%',
                      py: 5
                    }}>
                      <Typography sx={{ color: '#94A3B8', mb: 2, textAlign: 'center' }}>
                        No ad variants generated yet.
                      </Typography>
                      <Typography sx={{ color: '#64748B', textAlign: 'center' }}>
                        Enter your product information and click "Generate Ad Variants" to see results.
                      </Typography>
                    </Box>
                  ) : isLoading ? (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '100%',
                      py: 5
                    }}>
                      <CircularProgress size={40} color="primary" />
                      <Typography sx={{ color: '#94A3B8', mt: 2, textAlign: 'center' }}>
                        Generating ad variants...
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={3}>
                      {adVariants.map((variant) => (
                        <Grid item xs={12} key={variant.title}>
                          <Card sx={{ backgroundColor: '#0F172A', borderRadius: 2 }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="h6" sx={{ color: 'white' }}>
                                  {variant.title}
                                </Typography>
                                <Tooltip title={copiedId === variant.title ? "Copied!" : "Copy ad"}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleCopyAd(variant.title)}
                                    sx={{ color: copiedId === variant.title ? '#10B981' : '#94A3B8' }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Typography sx={{ color: '#94A3B8', mb: 2 }}>
                                {variant.description}
                              </Typography>
                              <Button 
                                variant="contained" 
                                size="small"
                                sx={{ 
                                  backgroundColor: '#3B82F6',
                                  '&:hover': { backgroundColor: '#2563EB' },
                                }}
                              >
                                {variant.cta}
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <AdAnalytics role="advertiser" />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <PromptSelector
            selectedPrompt={selectedPrompt}
            onPromptChange={setSelectedPrompt}
          />
          <PlacementSelector
            selectedPlacement={selectedPlacement}
            onPlacementChange={setSelectedPlacement}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={isLoadingPreview || !selectedPrompt || !selectedPlacement}
            >
              {isLoadingPreview ? <CircularProgress size={24} color="inherit" /> : 'Generate Preview'}
            </Button>
          </Box>
          {previewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {previewError}
            </Alert>
          )}
          {previewData && (
            <Box sx={{ mt: 3 }}>
              <LLMResponseRenderer previewData={previewData} />
            </Box>
          )}
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 3, 
                  backgroundColor: '#1E293B',
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <Typography variant="h6" sx={{ color: 'white', mb: 3 }}>
                  AI Agent Configuration
                </Typography>

                {agentError && (
                  <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}>
                    {agentError}
                  </Alert>
                )}

                <AgentTaskSelector
                  selectedTask={selectedTask}
                  onTaskChange={handleTaskChange}
                />

                <Button 
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleGenerateAgentOutput}
                  disabled={!selectedTask || isAgentLoading}
                  sx={{ 
                    backgroundColor: '#3B82F6',
                    '&:hover': { backgroundColor: '#2563EB' },
                    '&.Mui-disabled': { backgroundColor: 'rgba(59, 130, 246, 0.3)' }
                  }}
                >
                  {isAgentLoading ? <CircularProgress size={24} color="inherit" /> : 'Generate Agent Output'}
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              {agentOutput ? (
                <AgentOutputRenderer output={agentOutput} />
              ) : (
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 3, 
                    backgroundColor: '#1E293B',
                    borderRadius: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px'
                  }}
                >
                  <Typography sx={{ color: '#94A3B8', mb: 2, textAlign: 'center' }}>
                    No agent output generated yet.
                  </Typography>
                  <Typography sx={{ color: '#64748B', textAlign: 'center' }}>
                    Select a task and click "Generate Agent Output" to see the results.
                  </Typography>
                </Paper>
              )}
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default AdvertiserPlayground; 